import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, LogOut, Plus, Repeat } from 'lucide-react';
import { useTranslation } from '@/core/i18n';
import { useLocationUrl } from '@/shared/hooks/useLocationUrl';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/theme/components/ui/command';
import { breadcrumbRegistry } from '@/core/layout/breadcrumbRegistry';

interface PageEntry {
  path: string;
  labelKey: string;
  fallback: string;
}

function buildPageEntries(): PageEntry[] {
  return Object.entries(breadcrumbRegistry)
    .filter(([path]) => !path.includes(':'))
    .map(([path, entry]) => ({ path, labelKey: entry.labelKey, fallback: entry.fallback }));
}

/**
 * Command palette — global ⌘K / Ctrl+K search for pages and actions.
 *
 * Scaffolding only (Phase 1.5): pages are derived from the breadcrumb
 * registry (same source of truth as the breadcrumbs); actions cover the
 * basic set (new booking, switch context, sign out). Fuzzy filtering
 * comes from cmdk.
 */
export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locUrl = useLocationUrl();
  const [open, setOpen] = useState(false);

  const pages = useMemo(() => buildPageEntries(), []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSelect = useCallback(
    (path: string) => {
      setOpen(false);
      // Inject the active :locationId into /location/* targets (scope-in-URL).
      // Non-location paths like /select and /logout pass through unchanged.
      navigate(locUrl(path));
    },
    [navigate, locUrl],
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder={t('commandPalette.placeholder', 'Search pages, actions, or type a command…')}
      />
      <CommandList>
        <CommandEmpty>
          {t('commandPalette.empty', 'No results. Try a different keyword.')}
        </CommandEmpty>

        <CommandGroup heading={t('commandPalette.groups.pages', 'Pages')}>
          {pages.map((page) => (
            <CommandItem
              key={page.path}
              value={`${t(page.labelKey, page.fallback)} ${page.path}`}
              onSelect={() => handleSelect(page.path)}
            >
              <ArrowRight className="h-4 w-4 text-sand-500" aria-hidden="true" />
              <span className="flex-1">{t(page.labelKey, page.fallback)}</span>
              <span className="font-mono text-[10px] text-sand-500">{page.path}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t('commandPalette.groups.actions', 'Actions')}>
          <CommandItem
            value="new booking"
            onSelect={() => handleSelect('/location/bookings/new')}
          >
            <Plus className="h-4 w-4 text-ocean-600" aria-hidden="true" />
            <span className="flex-1">{t('commandPalette.actions.newBooking', 'New booking')}</span>
          </CommandItem>
          <CommandItem
            value="switch location context"
            onSelect={() => handleSelect('/select')}
          >
            <Repeat className="h-4 w-4 text-sun-600" aria-hidden="true" />
            <span className="flex-1">{t('commandPalette.actions.switchLocation', 'Switch location')}</span>
          </CommandItem>
          <CommandItem
            value="sign out logout"
            onSelect={() => handleSelect('/logout')}
          >
            <LogOut className="h-4 w-4 text-coral-600" aria-hidden="true" />
            <span className="flex-1">{t('commandPalette.actions.signOut', 'Sign out')}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
